import { useEffect, useState } from 'react'

import { isFeatureEnabled } from '@src/utils/environment'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('useBitcoinPrice')

/**
 * Hook that fetches the current Bitcoin price in USD from a public API
 * This is used as an interim solution until the backend supports Bitcoin price data
 */
export const useBitcoinPrice = () => {
    const [price, setPrice] = useState<number | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        const fetchBitcoinPrice = async () => {
            try {
                setLoading(true)
                // Using CoinGecko's free API - no API key required for basic usage
                const response = await fetch(
                    'https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd'
                )

                if (!response.ok) {
                    throw new Error(`Failed to fetch Bitcoin price: ${response.status}`)
                }

                const data = await response.json()
                if (data?.bitcoin?.usd) {
                    setPrice(data.bitcoin.usd)
                    logger.info('Bitcoin price fetched successfully:', data.bitcoin.usd)
                } else {
                    throw new Error('Invalid response format from API')
                }
            } catch (err) {
                logger.error('Error fetching Bitcoin price:', err)
                setError(err instanceof Error ? err.message : String(err))
                // Set a fallback price so the UI doesn't break
                setPrice(30000) // Fallback price if API fails
            } finally {
                setLoading(false)
            }
        }

        if (isFeatureEnabled('ENABLE_FEATHER')) {
            fetchBitcoinPrice()

            // Refresh price every 60 seconds
            const intervalId = setInterval(fetchBitcoinPrice, 60000)

            return () => {
                clearInterval(intervalId)
            }
        } else {
            // No-op when Feather feature is disabled
            setPrice(0)
            setLoading(false)
            return undefined
        }
    }, [])

    return { price, loading, error }
}
